import React from 'react';
import './Privacy.css';

const Privacy: React.FC = () => {
  return (
    <div className="privacy-container">
      <div className="privacy-content">
        <header className="privacy-header">
          <div className="privacy-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z" fill="#4b9ca2"/>
              <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="privacy-title">Privacy Policy</h1>
          <div className="privacy-meta">
            <p><strong>URL:</strong> <code>https://soundlegal.ai/privacy</code></p>
            <p><strong>Last updated:</strong> [●]</p>
          </div>
        </header>

        <div className="privacy-body">
          <section className="privacy-intro">
            <p>
              <strong>SoundLegal, Inc. ("SoundLegal," "we," "us")</strong> provides AI-enabled contract analysis and contract generation services (the "Service"). We are privacy-first: <strong>no default training on your uploads</strong>, encryption in transit and at rest, and clear user controls.
            </p>
            <p>
              By using the Service, you agree to this Privacy Policy. If you do not agree, do not use the Service.
            </p>
          </section>

          <section className="privacy-section">
            <h2>What We Collect</h2>
            <ul>
              <li><strong>Account & Identity:</strong> Name, email, organization, role, billing metadata.</li>
              <li><strong>Content:</strong> Documents you upload and the <strong>Outputs</strong> generated by the Service.</li>
              <li><strong>Usage & Device:</strong> IP address, device/browser type, pages/actions, timestamps, diagnostics, crash logs.</li>
              <li><strong>Payments:</strong> Processed by <strong>Stripe</strong>; we retain transaction metadata (not full card numbers).</li>
              <li><strong>Cookies/Analytics:</strong> Functional cookies; limited analytics (e.g., Google Analytics). IPs may be anonymized where supported.</li>
            </ul>
            <p>We do <strong>not</strong> target or knowingly collect data from children under 13 (or under 16 in the EEA/UK).</p>
          </section>

          <section className="privacy-section">
            <h2>How We Use Data</h2>
            <ul>
              <li><strong>Operate the Service:</strong> Process uploads to generate Outputs, provide history, search, collaboration, and support.</li>
              <li><strong>Security:</strong> Monitor, prevent, and investigate fraud, abuse, or security incidents.</li>
              <li><strong>Product Improvement:</strong> Aggregate/ de-identify telemetry to improve reliability and UX.</li>
              <li><strong>AI Training (Opt-In Only):</strong> We will <strong>never</strong> train on your uploads unless you <strong>explicitly enable</strong> <strong>Training Consent</strong>. You can revoke at any time; revocation is <strong>prospective</strong>.</li>
              <li><strong>Communications:</strong> Service notices, security alerts, support responses, optional product updates.</li>
              <li><strong>Legal Compliance:</strong> As required by applicable law, court orders, or to protect rights/safety.</li>
            </ul>
            <p>We <strong>do not sell</strong> personal data.</p>
          </section>

          <section className="privacy-section">
            <h2>Lawful Bases (GDPR/UK GDPR, where applicable)</h2>
            <ul>
              <li><strong>Contract:</strong> To deliver the Service.</li>
              <li><strong>Legitimate Interests:</strong> Security, troubleshooting, service analytics (balanced and privacy-protective).</li>
              <li><strong>Consent:</strong> AI training (opt-in), certain cookies/marketing.</li>
              <li><strong>Legal Obligation:</strong> To comply with law or lawful requests.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Sharing</h2>
            <ul>
              <li><strong>Subprocessors (Processors):</strong>
                <ul>
                  <li><strong>Google Cloud Platform (GCP):</strong> hosting, storage, backups, logging.</li>
                  <li><strong>Google Vertex AI / Gemini:</strong> model inference.</li>
                  <li><strong>Stripe:</strong> billing & payments.</li>
                </ul>
                <p>See current list at <strong><a href="https://soundlegal.ai/subprocessors" target="_blank" rel="noopener noreferrer">https://soundlegal.ai/subprocessors</a></strong>.</p>
              </li>
              <li><strong>Affiliates/Corporate Transactions:</strong> Only as necessary and subject to equivalent protections.</li>
              <li><strong>Legal:</strong> To comply with law, enforce Terms, or protect rights/safety.</li>
              <li><strong>At Your Direction:</strong> Integrations you connect.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Retention & Deletion</h2>
            <ul>
              <li><strong>Uploads/Outputs:</strong> Retained for your use and controls; you can delete anytime.</li>
              <li><strong>Operational caching:</strong> Typically &lt;24h; security/abuse telemetry up to 7 days.</li>
              <li><strong>Backups:</strong> Rotating encrypted backups; deleted by rotation schedule.</li>
              <li><strong>Verified deletion requests:</strong> Processed within <strong>72 hours</strong>, subject to legal holds.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>International Transfers</h2>
            <p>We are U.S.-based. For EU/UK data, we rely on <strong>SCCs (Module 2)</strong> and the <strong>UK IDTA</strong> (or Addendum) and implement supplementary safeguards (e.g., encryption).</p>
          </section>

          <section className="privacy-section">
            <h2>Your Rights</h2>
            <ul>
              <li><strong>Access / Portability / Correction / Deletion.</strong></li>
              <li><strong>Restriction / Objection</strong> (where applicable).</li>
              <li><strong>Withdraw Consent</strong> (e.g., training, marketing).</li>
              <li><strong>CCPA/CPRA (California):</strong> Right to know/delete/correct; <strong>no sale or sharing</strong> of personal info.</li>
            </ul>
            <p>Request at <strong><a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></strong>. We will verify your identity before fulfilling.</p>
          </section>

          <section className="privacy-section">
            <h2>Security</h2>
            <ul>
              <li><strong>Encryption:</strong> TLS 1.3 in transit; AES-256 at rest.</li>
              <li><strong>Access Controls:</strong> Role-based, least privilege, MFA for admin access.</li>
              <li><strong>Monitoring & Testing:</strong> Centralized logging, vulnerability scans, annual third-party testing.</li>
              <li><strong>Vendor Management:</strong> Contractual DPAs; security due diligence; subprocessor list public.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Cookies & Analytics</h2>
            <p>We use essential cookies for login/session and limited analytics to improve UX. You may control cookies in your browser; some features may not work without them.</p>
          </section>

          <section className="privacy-section">
            <h2>Changes</h2>
            <p>We may update this Policy. <strong>Material</strong> changes will be announced in-product or by email <strong>≥15 days</strong> before effectiveness (unless security/legal urgency requires sooner).</p>
          </section>

          <hr className="privacy-divider" />

          <section className="privacy-section privacy-contact">
            <h2>Contact</h2>
            <div className="contact-grid">
              <div className="contact-item">
                <h3>General Privacy Inquiries</h3>
                <p><strong>Email:</strong> <a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></p>
                <p><strong>Response Time:</strong> Within 24-48 hours</p>
              </div>
              <div className="contact-item">
                <h3>Data Protection Requests</h3>
                <p><strong>Email:</strong> <a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></p>
                <p><strong>Subject Line:</strong> "Data Protection Request"</p>
              </div>
              <div className="contact-item">
                <h3>DMCA Notices</h3>
                <p><strong>URL:</strong> <a href="https://soundlegal.ai/dmca" target="_blank" rel="noopener noreferrer">https://soundlegal.ai/dmca</a></p>
                <p><strong>For copyright-related matters</strong></p>
              </div>
            </div>
            <div className="privacy-footer-note">
              <p><strong>Important:</strong> For data protection requests, please include your account email and specify the type of request (access, deletion, correction, etc.). We will verify your identity before processing any requests.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
